import React, {Suspense} from "react";
import {createBrowserRouter} from "react-router-dom";
import {RouteObject} from "react-router-dom";

import {getActualites} from "@/api/actualite";
import {getNavigation} from "@/api/navigation";
import Content from "@/components/Content/Content";
import ContentOnglet from "@/components/ContentOnglet/ContentOnglet";
import FilAriane from "@/components/FilAriane/FilAriane";
import Layout from "@/components/Layout/Layout";
import Loader from "@/components/Loader/loader";
import Accueil from "@/page/Accueil";
import Actualites from "@/page/Actualites";
import FournisseurDetail from "@/page/FournisseurDetail";
import Fournisseurs from "@/page/Fournisseurs";
import {actualite} from "@/types/actualite";
import {navParent} from "@/types/navigation";

const ReferentielArticle = React.lazy(
  () => import("@/page/ReferentielArticle"),
);
const CofaqEmploye = React.lazy(() => import("@/page/CofaqEmploye"));
const OperationsCommerciales = React.lazy(
  () => import("@/page/OperationsCommerciales"),
);
const Evenements = React.lazy(() => import("@/page/Evenements"));

// Récupère les page de niveau 1 dans l'arborescence;
const getNiveau1PageRoute = (navigation: navParent[] | null): RouteObject[] => {
  const allPage: RouteObject[] = [];
  navigation?.forEach((pageNv1, index) => {
    allPage.push({
      path: pageNv1.link,
      element: (
        <Layout key={index}>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[{libelle: pageNv1.libelle, link: pageNv1.link}]}
            />
            <div className="allOnglet">
              {pageNv1.children?.map((child, index) => {
                return (
                  <ContentOnglet
                    title={child.libelle}
                    link={child.link}
                    key={`${child.libelle} ${index}`}
                  />
                );
              })}
            </div>
          </Suspense>
        </Layout>
      ),
    });
  });
  return allPage;
};

// Récupère les page de niveau 2 dans l'arborescence;
const getNiveau2pageRoute = (navigation: navParent[] | null): RouteObject[] => {
  const allPage: RouteObject[] = [];

  navigation?.forEach(pageNv1 => {
    const allPageNv2: RouteObject[] | undefined = pageNv1?.children?.map(
      (element, index) => {
        return {
          path: element.link,
          element: (
            <Layout key={index}>
              <Suspense fallback={<Loader />}>
                <FilAriane
                  arborescence={[
                    {libelle: pageNv1.libelle, link: pageNv1.link},
                    {libelle: element.libelle, link: element.link},
                  ]}
                />
                <ContentOnglet title={element.libelle} link={element.link}>
                  <Content idPage={element.id} />
                </ContentOnglet>
              </Suspense>
            </Layout>
          ),
        };
      },
    );
    if (allPageNv2) {
      allPage.push(...allPageNv2);
    }
  });
  return allPage;
};
const getPageRoute = async (): Promise<RouteObject[]> => {
  const navigation: navParent[] = await getNavigation();
  return [
    ...getNiveau1PageRoute(navigation),
    ...getNiveau2pageRoute(navigation),
  ];
};
const getActualitesRoute = async (): Promise<RouteObject[]> => {
  const allActu: actualite[] = await getActualites();
  const routageActu: RouteObject[] = allActu.map(actu => {
    return {
      path: `/actualite/${actu.id}`,
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[
                {libelle: "Groupe Cofaq", link: "/groupe_cofaq"},
                {libelle: "Actualités", link: "/actualites"},
                {libelle: actu.title, link: `/actualites/${actu.id}`},
              ]}
            />
            <ContentOnglet title={actu.title}>
              <Content idArticle={actu.id} />
            </ContentOnglet>
          </Suspense>
        </Layout>
      ),
    };
  });
  return routageActu;
};

async function createRouter(): Promise<ReturnType<typeof createBrowserRouter>> {
  return createBrowserRouter([
    {
      path: "/",
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <Accueil />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/referentiel-articles",
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[
                {libelle: "Référentiel article", link: "/referentiel-article"},
              ]}
            />
            <ReferentielArticle />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/Actualites",
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[
                {libelle: "Groupe Cofaq", link: "/groupe_cofaq"},
                {libelle: "Actualités", link: "/actualites"},
              ]}
            />
            <Actualites />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/annuaire_cofaq",
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[
                {libelle: "Groupe Cofaq", link: "/groupe_cofaq"},
                {libelle: "Annuaire COFAQ", link: "/annuaire_cofaq"},
              ]}
            />
            <CofaqEmploye />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/catalogues",
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[
                {libelle: "Support à la vente", link: "/support_a_la_vente"},
                {
                  libelle: "Operations Commerciales",
                  link: "/catalogues",
                },
              ]}
            />
            <OperationsCommerciales />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/evenements",
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[
                {libelle: "Groupe Cofaq", link: "/groupe_cofaq"},
                {
                  libelle: "Evenements",
                  link: "/evenements",
                },
              ]}
            />
            <Evenements />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/fournisseurs",
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[
                {libelle: "Achat", link: "/achat"},
                {
                  libelle: "Fournisseurs",
                  link: "/fournisseurs",
                },
              ]}
            />
            <Fournisseurs />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/fournisseur/:id",
      element: (
        <Layout>
          <Suspense fallback={<Loader />}>
            <FilAriane
              arborescence={[
                {libelle: "Achat", link: "/achat"},
                {
                  libelle: "Fournisseurs",
                  link: "/fournisseurs",
                },
              ]}
            />
            <FournisseurDetail />
          </Suspense>
        </Layout>
      ),
    },
    ...(await getActualitesRoute()),
    ...(await getPageRoute()),
  ]);
}

export const router = createRouter();
